import ProfessionalSchedule from './pages/ProfessionalSchedule';
import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Link, Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import Favorites from './pages/Favorites';
import BusinessProfile from './pages/BusinessProfile';
import Login from './pages/Login';
import Register from './pages/Register';
import BusinessSignup from './pages/BusinessSignup';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Appointment from './pages/Appointment';
import AdminDashboard from './pages/AdminDashboard';
import OwnerDashboard from './pages/OwnerDashboard';
import AcceptInvite from './pages/AcceptInvite';
import ChangePassword from './pages/ChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Security from './pages/Security';
import TwoFactorVerify from './pages/TwoFactorVerify';
import Contact from './pages/Contact';
import MarketingDashboard from './pages/MarketingDashboard';
import Footer from './components/Footer';
import { PricingPage, AgreementPage, PrivacyPage, MarketplacePoliciesPage } from './pages/PublicInfo';
import './App.css';
import './i18n';

function InstallAppButton() {
  const [prompt, setPrompt] = useState(null);
  const [message, setMessage] = useState('');
  const buttonRef = useRef(null);
  const [messagePosition, setMessagePosition] = useState({ top: 0, left: 0 });
  useEffect(() => {
    const onPrompt = (event) => { event.preventDefault(); setPrompt(event); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);
  const install = async () => {
    if (!prompt) {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (rect) setMessagePosition({ top: rect.bottom + 8, left: rect.left });
      const userAgent = window.navigator.userAgent || '';
      const isIOS = /iPhone|iPad|iPod/i.test(userAgent) || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
      const isSafari = /Safari/i.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome|Chromium|Firefox|Edg/i.test(userAgent);
      const isMacSafari = !isIOS && isSafari && /Macintosh|Mac OS X/i.test(userAgent);
      if (isIOS && isSafari && window.navigator.standalone) {
        setMessage('Elegance Booking is already installed on your Home Screen.');
      } else if (isIOS && isSafari) {
        setMessage('In Safari, tap Share, then Add to Home Screen.');
      } else if (isMacSafari) {
        setMessage('In Safari, choose File > Add to Dock to install the app.');
      } else {
        setMessage('Open your browser menu and choose Install app or Add to Home Screen.');
      }
      window.setTimeout(() => setMessage(''), 6000);
      return;
    }
    await prompt.prompt();
    setPrompt(null);
  };
  return <span className="install-app-wrap"><button ref={buttonRef} type="button" className="link-button install-app-button" onClick={install}>Install app</button>{message && <span className="install-app-message" style={{ top: messagePosition.top, left: messagePosition.left }} role="status">{message}</span>}</span>;
}

function AppShell() {
  const [userInfo, setUserInfo] = useState(() => JSON.parse(localStorage.getItem('userInfo') || 'null'));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const syncAuth = () => setUserInfo(JSON.parse(localStorage.getItem('userInfo') || 'null'));
    window.addEventListener('storage', syncAuth);
    window.addEventListener('focus', syncAuth);
    window.addEventListener('authchange', syncAuth);
    return () => {
      window.removeEventListener('storage', syncAuth);
      window.removeEventListener('focus', syncAuth);
      window.removeEventListener('authchange', syncAuth);
    };
  }, []);

  const logout = () => {
    localStorage.removeItem('userInfo');
    setUserInfo(null);
    window.location.href = '/';
  };

  const businessRoles = userInfo?.businessRoles?.map((membership) => membership.role) || [];
  const effectiveBusinessRole = businessRoles.includes('owner') ? 'owner' : businessRoles.includes('manager') ? 'manager' : businessRoles.includes('staff') ? 'staff' : null;
  const roleLabel = !userInfo ? 'Guest' : userInfo.role === 'admin' ? 'Admin' : effectiveBusinessRole === 'owner' ? 'Owner' : effectiveBusinessRole === 'manager' ? 'Manager' : userInfo.role === 'staff' ? 'Staff' : 'Customer';
  const canManageBusinesses = userInfo && (userInfo.role === 'admin' || ['owner', 'manager'].includes(effectiveBusinessRole));
  const canSeePricing = !userInfo || userInfo.role === 'admin' || ['owner', 'manager'].includes(effectiveBusinessRole);
  const canCreateManualAppointment = userInfo && ['owner', 'manager'].includes(effectiveBusinessRole);
  const canSeeCustomerBooking = !userInfo || userInfo.role === 'admin' || (!effectiveBusinessRole && userInfo.role !== 'staff');

  return (
    <div className="App">
      <header className="topbar">
        <Link className="brand brand-nav" to="/" aria-label="Elegance Booking"><img className="nav-brand-logo" src="/elegance-booking-logo-transparent.png" alt="Elegance Booking" /></Link>
        <div className="topbar-meta">
          {userInfo ? <span className="auth-badge">{userInfo.name || userInfo.email || 'User'} · {roleLabel}</span> : <span className="auth-badge auth-badge-muted">Not signed in</span>}
        </div>
        <button
          type="button"
          className="nav-toggle"
          aria-label={mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen((open) => !open)}
        >
          <span aria-hidden="true"></span><span aria-hidden="true"></span><span aria-hidden="true"></span>
        </button>
        <nav className={`nav-links${mobileNavOpen ? ' nav-open' : ''}`} onClick={() => setMobileNavOpen(false)}>
          <InstallAppButton />
          <NavLink to="/">Marketplace</NavLink>
          {canCreateManualAppointment ? <NavLink to="/owner#owner-appointments">Manage bookings</NavLink> : canSeeCustomerBooking && <NavLink to={userInfo ? '/appointment' : '/login?returnTo=%2Fappointment'}>Book</NavLink>}
          <NavLink to="/contact">Contact us</NavLink>
          {canSeePricing && <NavLink to="/pricing">Pricing</NavLink>}
          {userInfo && <NavLink to="/dashboard">My Appointments</NavLink>}
          {userInfo && canSeeCustomerBooking && <NavLink to="/favorites">My Favorites</NavLink>}
          {userInfo && (effectiveBusinessRole || userInfo.role === 'staff') && <NavLink to="/my-schedule">My schedule</NavLink>}
          {userInfo && <NavLink to="/security">Security</NavLink>}
          {canManageBusinesses && <NavLink to="/owner">Owner</NavLink>}
          {canManageBusinesses && <NavLink to="/marketing">Marketing</NavLink>}
          {userInfo?.role === 'admin' && <NavLink to="/admin">Admin</NavLink>}
          {!userInfo && <NavLink to="/login">Login</NavLink>}
          {!userInfo && <NavLink className="nav-cta" to="/register">Register</NavLink>}
          {userInfo && <button className="link-button" onClick={logout}>Logout</button>}
        </nav>
      </header>
      <div className="brand-masthead"><Link to="/" aria-label="Elegance Booking"><img className="brand-logo" src="/elegance-booking-logo-transparent.png" alt="Elegance Booking" /></Link></div>
      <main>
        <Routes>
          <Route path="/my-schedule" element={<ProfessionalSchedule />} />
          <Route path="/" element={<Home />} />
          <Route path="/favorites" element={!userInfo ? <Navigate to="/login?returnTo=%2Ffavorites" replace /> : canSeeCustomerBooking ? <Favorites /> : <Navigate to="/" replace />} />
          <Route path="/business/:slug" element={<BusinessProfile />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/business-signup" element={<BusinessSignup />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/appointments" element={<Dashboard />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/owner" element={<OwnerDashboard />} />
          <Route path="/marketing" element={<MarketingDashboard />} />
          <Route path="/appointment" element={<Appointment />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/security" element={<Security />} />
          <Route path="/two-factor" element={<TwoFactorVerify />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/pricing" element={canSeePricing ? <PricingPage /> : <Navigate to="/" replace />} />
          <Route path="/business-agreement" element={<AgreementPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/marketplace-policies" element={<MarketplacePoliciesPage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function ScrollToRoute() {
  const { pathname, search, hash, key } = useLocation();

  useEffect(() => {
    if (hash) {
      window.requestAnimationFrame(() => document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' }));
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, search, hash, key]);

  return null;
}

function App() {
  return <Router><ScrollToRoute /><AppShell /></Router>;
}

export default App;
